/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState, useRef } from 'react'
import { DropdownMenuProps } from './DropdownMenu.types'
import {
  DropdownBackdrop,
  DropdownIcon,
  DropdownInput,
  DropdownList,
  DropdownListItem,
  DropdownListLabel,
  DropdownListWrapper,
  DropdownWrapper
} from './DropdownMenu.styles'
import { isString, sortByKey } from '../../../utils'
import useId from '../../hooks/useId'

const Dropdown = ({
  className = '',
  options = [],
  value = '',
  allOptionLabel = '',
  autocomplete = false,
  sortKey,
  color = 'primary',
  iconOpen = 'MdOutlineExpandLess',
  iconClose = 'MdOutlineExpandMore',
  styles,
  changeActions,
  hideOnEmpty = false,
  'data-testid': testid = 'dropdown-menu',
  ...props
}: DropdownMenuProps) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLDivElement>(null)
  const { getTestId } = useId(testid)

  const optionList = useMemo(() => {
    const filteredOptions = search.trim()
      ? options?.filter(({ name }) =>
          name.toLowerCase().includes(search.toLowerCase())
        )
      : options
    return sortKey ? sortByKey(filteredOptions, sortKey) : filteredOptions
  }, [options, sortKey, search])

  const currentValue =
    (!search.trim() && value) ||
    (!search.trim() && allOptionLabel && 'all') ||
    optionList?.[0]?.slug

  const currentLabel =
    currentValue === 'all'
      ? allOptionLabel
      : options?.find(({ slug }) => slug === currentValue)?.name || ''

  const iconOpenProps = isString(iconOpen) ? { name: iconOpen } : iconOpen
  const iconCloseProps = isString(iconClose) ? { name: iconClose } : iconClose
  const iconProps = open ? iconOpenProps : iconCloseProps

  const toggleDropdown = () => {
    setOpen(!open)
    if (open) {
      const input = inputRef.current?.firstChild as HTMLInputElement
      input.blur()
      setTimeout(() => setSearch(''), 250)
    }
  }

  const handleChange = (newValue: string) => {
    if (changeActions) {
      changeActions({ newValue })
    }
    toggleDropdown()
  }

  const handleSearch = (event: any) => {
    const searchTerm = event.target.value
    setSearch(searchTerm)
  }

  const handleEnter = ({ keyCode }: any) => {
    if (keyCode === 13) {
      const newValue = currentValue || value
      handleChange(newValue)
    }
  }
  const isHidden = hideOnEmpty && options.length === 0

  return !isHidden ? (
    <DropdownWrapper
      className={`DropdownWrapper ${className}`}
      sx={styles}
      data-testid={getTestId()}
      {...props}>
      <DropdownInput
        className="DropdownInput"
        ref={inputRef}
        value={search}
        placeholder={currentLabel}
        readOnly={!autocomplete}
        onClick={toggleDropdown}
        onChange={handleSearch}
        onKeyUp={handleEnter}
        inputMode="search"
        data-testid={getTestId('input')}
        endAdornment={<DropdownIcon {...iconProps} />}
      />
      <DropdownListWrapper
        className={`DropdownListWrapper ${open ? 'open' : ''}`}>
        <DropdownList className="DropdownList" data-testid={getTestId('list')}>
          {allOptionLabel && (
            <DropdownListItem
              color={color}
              className={`DropdownListItem ${
                currentValue === 'all' ? 'selected' : ''
              }`}
              data-testid={getTestId('all')}
              onClick={() => handleChange('all')}>
              <DropdownListLabel>{allOptionLabel}</DropdownListLabel>
            </DropdownListItem>
          )}
          {optionList?.map(({ name, slug }) => (
            <DropdownListItem
              key={slug}
              color={color}
              className={`DropdownListItem ${
                currentValue === slug ? 'selected' : ''
              }`}
              data-testid={getTestId(slug)}
              onClick={() => handleChange(slug)}>
              <DropdownListLabel className="DropdownListLabel">
                {name}
              </DropdownListLabel>
            </DropdownListItem>
          ))}
        </DropdownList>
      </DropdownListWrapper>
      <DropdownBackdrop
        open={open}
        onClick={toggleDropdown}
        data-testid="dropdown-menu-backdrop"
      />
    </DropdownWrapper>
  ) : null
}

export default Dropdown
